import { type CellContext, createColumnHelper } from '@tanstack/react-table';
import ActionCell from '@/components/table/cells/action-cell.component';
import TextAreaCell from '@/components/table/cells/text-area-cell.component';
import TextCell from '@/components/table/cells/text-cell.component';
import { useAppSelector } from '@/redux/hooks';
import { selectRowEditMode } from '@/redux/slices/table/selectors';
import type { Hazard } from '@/types/swms';

const columnHelper = createColumnHelper<Hazard>();

const createCellRenderer = <T,>(
  CellComponent: React.ComponentType<CellContext<Hazard, T> & { editMode?: boolean }>
) => {
  return (props: CellContext<Hazard, T>) => {
    const { row } = props;
    const editMode = useAppSelector((state) => selectRowEditMode(state, row.index));
    return <CellComponent {...props} editMode={editMode} />;
  };
};

export const columns = [
  columnHelper.accessor('stepNumber', {
    id: 'stepNumber',
    header: 'Step',
    cell: createCellRenderer(TextCell),
    meta: {
      type: 'number',
      min: 1,
      max: 10,
      width: 120,
      minWidth: 100,
      maxWidth: 140,
    },
  }),
  columnHelper.accessor('taskActivity', {
    id: 'taskActivity',
    header: 'Task/Activity',
    cell: createCellRenderer(TextCell),
    meta: {
      type: 'text',
      placeholder: 'Enter task/activity',
      width: '30%',
    },
  }),
  columnHelper.accessor('description', {
    id: 'description',
    header: 'Hazard',
    cell: createCellRenderer(TextCell),
    meta: {
      type: 'text',
      placeholder: 'Enter hazard',
      width: '30%',
    },
  }),
  columnHelper.accessor('controls', {
    id: 'controls',
    header: 'Control',
    cell: createCellRenderer(TextAreaCell),
    meta: {
      placeholder: 'Enter controls, one per line.',
      width: '30%',
    },
  }),
  columnHelper.accessor('initialRiskRating', {
    id: 'initialRiskRating',
    header: 'Initial Risk Rating',
    cell: createCellRenderer(TextCell),
    meta: {
      type: 'number',
      min: 1,
      max: 5,
      width: 170,
      minWidth: 170,
      maxWidth: 200,
    },
  }),
  columnHelper.accessor('residualRiskRating', {
    id: 'residualRiskRating',
    header: 'Residual Risk Rating',
    cell: createCellRenderer(TextCell),
    meta: {
      type: 'number',
      min: 1,
      max: 5,
      width: 170,
      minWidth: 170,
      maxWidth: 200,
    },
  }),
  columnHelper.display({
    id: 'actions',
    cell: createCellRenderer(ActionCell),
    meta: {
      width: 120,
      minWidth: 120,
      maxWidth: 120,
    },
  }),
];
